import React, { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import getCategoryKeyById from '../../../common/utils/getCategoryKeyById';
import getSearchTitleTranslationArgs from '../../utils/getSearchTitleTranslationArgs';
import styles from './SearchTitle.module.scss';

export type SearchTitleProps = {
  /** If geometry params exists (such as when dragging map)
   * ?nelat=35.48116&nelon=-81.28542&page=1&swlat=34.09025&swlon=-83.36942
   */
  hasGeometry: boolean;
  selectedCategories: { id: string }[];
  /** If url hasGeometry, specifically WITHOUT category-slug
   * http://dev.getmyboat.com/boat-rental/?nelat=35.48116&nelon=-81.28542&page=1&swlat=34.09025&swlon=-83.36942
    */
  isCustomSearch: boolean;
  /** This title is used in most cases. Can be modified in dev via Bridge http://dev.getmyboat.com/bridge/geo/editor/?url=http://dev.getmyboat.com/boat-rental/Miami--FL--United-States/ */
  h1FromRutterSEO?: string;
};

/** Props control which title is returned. Priority is:
 * 1. isCustomSearch always returns h1FromRutterSEO
 * 2. selectedCategories
 * 3. hasGeometry
 * 4. else fallback default title
 *
 * See story or `getSearchTitleTranslationArgs` for details
 */
const SearchTitle: FC<SearchTitleProps> = ({
  hasGeometry,
  selectedCategories,
  isCustomSearch,
  h1FromRutterSEO,
}) => {
  const [categoryTranslator] = useTranslation('boat-categories');
  const [t] = useTranslation('search-page');

  const categories = useMemo(() => selectedCategories.slice(0, 4).map((cat) => ({
    name: categoryTranslator(getCategoryKeyById(cat.id)),
    short_name: categoryTranslator(getCategoryKeyById(cat.id, 'short')),
  })), [selectedCategories, categoryTranslator]);
  const { key, interpolationOptions } = getSearchTitleTranslationArgs(categories, hasGeometry);
  const genericLocationTitle = t(key, interpolationOptions);
  return (
    <h1 className={styles.root}>
      {isCustomSearch ? genericLocationTitle : h1FromRutterSEO}
    </h1>
  );
};

export default SearchTitle;
